import React from 'react';
import { AlertTriangle, ChevronRight } from 'lucide-react';

function daysSince(d) {
  if (!d) return null;
  return Math.floor((Date.now() - new Date(d).getTime()) / 86400000);
}

function initials(first, last) {
  return `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase() || '?';
}

function StaleChip({ days }) {
  if (days == null) return <span style={{ color: '#DC3545', fontWeight: 700 }}>Never contacted</span>;
  const color = days > 21 ? '#DC3545' : days > 14 ? '#F26522' : '#ADB5BD';
  return <span style={{ color, fontWeight: days > 14 ? 700 : 400 }}>{days}d ago</span>;
}

export default function AtRiskLeadsSection({ rows, count, isTeam, onSeeAll, onRowClick }) {
  const visible = (rows || []).slice(0, 5);

  if (!visible.length) {
    return (
      <div className="my-day-card">
        <SectionHeader count={count} onSeeAll={onSeeAll} />
        <p style={{ fontSize: 12, color: '#6C757D', margin: '12px 0 0', textAlign: 'center' }}>No at-risk leads — great coverage!</p>
      </div>
    );
  }

  return (
    <div className="my-day-card">
      <SectionHeader count={count} onSeeAll={onSeeAll} />
      <div className="responsive-scroll">
        <table style={{ width: '100%', minWidth: 320, borderCollapse: 'collapse', fontSize: 12, marginTop: 8 }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Contact</th>
              <th style={th}>Last activity</th>
              <th style={th}>Stage</th>
              <th style={th}>Intent</th>
              {isTeam && <th style={th}>Owner</th>}
            </tr>
          </thead>
          <tbody>
            {visible.map(c => (
              <tr
                key={c.id}
                className={onRowClick ? 'my-day-row-clickable' : ''}
                onClick={onRowClick ? () => onRowClick(c) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(c); } } : undefined}
                style={{ borderTop: '1px solid #F0F0F0' }}
              >
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                    <div style={{ width: 26, height: 26, borderRadius: '50%', background: '#FFF3CD', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#F26522', flexShrink: 0 }}>
                      {initials(c.first_name, c.last_name)}
                    </div>
                    <span style={{ fontWeight: 500, color: '#212529' }}>{c.first_name} {c.last_name}</span>
                  </div>
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <StaleChip days={daysSince(c.last_contact_date)} />
                </td>
                <td style={{ ...td, textAlign: 'center' }}>
                  <span style={{ background: '#F8F9FA', color: '#6C757D', padding: '2px 7px', borderRadius: 8, fontSize: 11, textTransform: 'capitalize' }}>
                    {(c.pipeline_stage || 'new').replace(/_/g, ' ')}
                  </span>
                </td>
                <td style={{ ...td, textAlign: 'center', color: '#495057' }}>
                  {c.patient_intent_score != null ? `${c.patient_intent_score}/10` : '—'}
                </td>
                {isTeam && <td style={{ ...td, textAlign: 'center', color: '#6C757D' }}>{c.assignee_name || '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {onSeeAll && <SeeAllLink onSeeAll={onSeeAll} />}
    </div>
  );
}

function SectionHeader({ count, onSeeAll }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <AlertTriangle size={15} color="#F26522" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#212529' }}>At-risk leads</span>
        {count > 0 && <span className="my-day-count-badge" style={{ background: '#FFF3CD', color: '#F26522' }}>{count}</span>}
        {count === 0 && <span className="my-day-count-badge">0</span>}
      </div>
    </div>
  );
}

function SeeAllLink({ onSeeAll }) {
  return (
    <button onClick={onSeeAll} style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 10, background: 'none', border: 'none', color: '#2E8B98', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
      See all <ChevronRight size={13} />
    </button>
  );
}

const th = { padding: '4px 8px', fontWeight: 600, fontSize: 11, color: '#ADB5BD', textAlign: 'center' };
const td = { padding: '7px 8px', color: '#495057' };
